import React from "react";
import { useNavigate } from "react-router-dom";
import IntegrationActivityLog from "../IntegrationActivityLog";

type Tab = "overview" | "activity";

export default function TeamsPhone() {
  const [tab, setTab] = React.useState<Tab>("overview");

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "activity", label: "Activity" }
  ];

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center text-2xl">📞</div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Microsoft Teams Phone</h1>
          <p className="text-sm text-slate-500">Direct Routing / Calling Plans — Screen Pop + Call Logging</p>
        </div>
      </div>

      <div className="mb-5 p-4 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
        <strong>Setup required:</strong> Microsoft Teams Phone integration uses Microsoft Graph Communications APIs
        (Calling Plans or Direct Routing). Contact your Velaro account manager to enable this integration —
        it requires an Azure app registration with <code>Calls.AccessMedia.All</code> and <code>Calls.Initiate.All</code> permissions
        granted by your Microsoft 365 admin.
      </div>

      <div className="flex border-b border-slate-200 mb-6 gap-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          {[
            {
              icon: "📲", title: "Inbound call screen pop",
              desc: "When a Teams Phone call arrives, Velaro looks up the caller across your CRM and service integrations and surfaces contact name, history, and open tickets in the agent sidebar — before they answer."
            },
            {
              icon: "🔗", title: "Works with Direct Routing and Calling Plans",
              desc: "Supports both Microsoft Teams Direct Routing (SBC-based PSTN) and Microsoft Teams Calling Plans. No third-party PBX required."
            },
            {
              icon: "📋", title: "Call activity logging",
              desc: "When a call ends, Velaro creates an activity note on the matched CRM contact. Works with HubSpot, Salesforce, Dynamics 365, and Velaro CRM."
            },
            {
              icon: "🤖", title: "IVR hand-off",
              desc: "Calls handled by Velaro IVR can be transferred into Teams Phone queues. Conversation context travels with the call — agents see full transcript before speaking."
            }
          ].map(item => (
            <div key={item.title} className="border border-slate-200 rounded-lg p-4 flex gap-3">
              <span className="text-2xl">{item.icon}</span>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            </div>
          ))}
          <div className="mt-6 p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600">
            To get started, contact <strong>support@velaro.com</strong> or your account manager.
            A Velaro engineer will walk you through the Azure app registration and Graph API consent flow.
          </div>
        </div>
      )}

      {tab === "activity" && <IntegrationActivityLog integrationSlug="TeamsPhone" />}
    </div>
  );
}

export function BackToIntegrationLandingTeamsPhone() {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate("/integrations")}
      className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
      ← Integrations
    </button>
  );
}
